<script lang="ts">
    import { getContext, onDestroy } from "svelte";
    import type { Writable } from "svelte/store";
    import { CellState, type Puzzle, type Timer } from "@lib/types";
    import UndoIcon from "@icons/Undo.svelte";
    import RedoIcon from "@icons/Redo.svelte";
    import { validatePlacements } from "@lib/util";
    import { logEvent } from "@lib/events";

    let puzzle = getContext<Puzzle>("puzzle");
    let session = getContext<string>("session");
    let timer = getContext<Writable<Timer>>("timer");
    let placements = getContext<Writable<Array<CellState>>>("placements");
    let snapshots = getContext<Writable<Array<Array<CellState>>>>("snapshots");

    let resetStack: boolean = $state(true);
    let redoStack: Array<Array<CellState>> = $state([]);

    let undoAvailable = $derived($snapshots.length > 0);
    let redoAvailable = $derived(redoStack.length > 0);

    const canonicalState = (v: number) => {
        return v === CellState.StarInvalid ? CellState.Star : v;
    };

    const computeChanges = (before: Array<CellState>, after: Array<CellState>) => {
        const changes: Array<{ index: number; from: number; to: number }> = [];
        const n = Math.min(before.length, after.length);
        for (let i = 0; i < n; i++) {
            const from = canonicalState(before[i]);
            const to = canonicalState(after[i]);
            if (from !== to) {
                changes.push({ index: i, from, to });
            }
        }
        return changes;
    };

    onDestroy(
        placements.subscribe((_p) => {
            if (resetStack) {
                redoStack = [];
            }
            resetStack = true;
        }),
    );

    const applySnapshot = (snapshot: Array<CellState>) => {
        return validatePlacements(
            puzzle,
            snapshot,
        );
    };

    const undo = () => {
        let didUndo = false;
        let changes: Array<{ index: number; from: number; to: number }> = [];
        placements.update((p) => {
            const before = p.slice();
            const previous = $snapshots.pop();
            if (previous !== undefined) {
                didUndo = true;
                redoStack.push($state.snapshot(p));
                p = applySnapshot(previous);
            }
            changes = computeChanges(before, p);
            resetStack = false;
            return p;
        });

        if (didUndo) {
            void logEvent({
                session,
                event_type: "undo",
                elapsed_ms: $timer.elapsed,
                data: { changes },
            });
        }
    };

    const redo = () => {
        let didRedo = false;
        let changes: Array<{ index: number; from: number; to: number }> = [];
        placements.update((p) => {
            const before = p.slice();
            const next = $state.snapshot(redoStack.pop());
            if (next !== undefined) {
                didRedo = true;
                $snapshots.push($state.snapshot(p));
                p = applySnapshot(next);
            }
            changes = computeChanges(before, p);
            resetStack = false;
            return p;
        });

        if (didRedo) {
            void logEvent({
                session,
                event_type: "redo",
                elapsed_ms: $timer.elapsed,
                data: { changes },
            });
        }
    };
</script>

<div>
    <button class="icon" disabled={!undoAvailable} onclick={undo}>
        <UndoIcon />
    </button>
    <button class="icon" disabled={!redoAvailable} onclick={redo}>
        <RedoIcon />
    </button>
</div>
